import React, {useEffect, useState} from 'react';
import { IGlobalStorefrontSettings, PAYMENT_PROCESSOR_KEYS } from '../../models/Storefront';
import { IDropdownOption, Label, Stack, TextField} from '@fluentui/react';
import FontPicker from '../Inputs/FontPicker';
import CompactColorPicker from '../Inputs/CompactColorPicker';
import NumberField from '../Inputs/NumberField';
import MarkupTypeSelection from './MarkupTypeSelection';
import CurrencySelection from '../CurrencySelection/CurrencySelection';
import StorefrontThemeSelection from './StorefrontThemeSelection';
import StorefrontDropdown from './StorefrontDropdown';
import PaymentProcessorDropdown from './PaymentProcessorDropdown';
import {Dropdown} from "@fluentui/react/lib/Dropdown";
import {
    useGetTaxCodesQuery,
    useGetVendorsQuery
} from "../../store/Api";
import { 
    useGetOrganizationFeatureSettingsQuery,
    useGetOrganizationQuery
} from "../../store/endpoints/OrganizationsEndpoints";
import BooleanDropdown from "./BooleanDropdown";
import DataField from "../Common/DataField";
import ShippingChargeSelection from './ShippingChargeSelection';
import {getCurrencySymbol} from '../../logic/currencyLocalizationHelper';
import Accordion from "../Common/Accordion";
import OrderEmailTemplateField from './OrderEmailTemplateField';
import RichTextField from "../Common/RichTextField";
import ImageEditor from "../Inputs/ImageEditor";
import ToggleField from "../Common/ToggleField";
import {useSelector} from "react-redux";
import {RootState} from "../../store/rootReducer";

interface IGlobalStorefrontSettingsFormProps {
    globalSettings: IGlobalStorefrontSettings;
    onChange: (settings: IGlobalStorefrontSettings) => void;
}

const GlobalStorefrontSettingsForm: React.FC<IGlobalStorefrontSettingsFormProps> = ({globalSettings, onChange}) => {
    const [defaultCatalog, setDefaultCatalog] = useState<string>();
    const selectedOrganization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const organization = useGetOrganizationQuery(selectedOrganization ?? '', {skip: !selectedOrganization || selectedOrganization === ''});
    const orgFeatures = useGetOrganizationFeatureSettingsQuery(selectedOrganization ?? '', {skip: !selectedOrganization || selectedOrganization === ''})
    const taxCodes = useGetTaxCodesQuery();
    // The stock order confirmation, so the editor opens on real content rather than a blank box, and
    useEffect(() => {
        if(organization.data) {
            setDefaultCatalog(organization.data.defaultCatalog);
        }
    },[organization.data]);

    const vendors = useGetVendorsQuery(defaultCatalog ?? "", {skip: !defaultCatalog})
    
    const onChangeVendors = (event: React.FormEvent<HTMLDivElement>, item?: IDropdownOption): void => {
        if (item && globalSettings.storefrontVendors) {
            onChange({...globalSettings, storefrontVendors: item.selected? [...globalSettings.storefrontVendors, item.key as string] : globalSettings.storefrontVendors.filter(key => key!== item.key) });
        }
    };
    const filterStrategyOptions = () => {
        return [
            {key: 'exclude', text: 'Exclude'},
            {key: 'include', text: 'Include'},
        ]
    }

    return <div style={{ width: '100%', minWidth: '300px'}}>
        <h2>Global Settings</h2>
        <Stack tokens={{childrenGap: '1em'}}>
            <StorefrontDropdown label='Default Storefront Sub URL' selected={globalSettings.defaultStorefrontSubUrl} onChange={(v) => onChange({...globalSettings, defaultStorefrontSubUrl: v})} defaultLabel={'NOT SET'} />
            <TextField label='Name' value={globalSettings.name} onChange={(e, v) => onChange({...globalSettings, name: v})} />
            <TextField label='Title' value={globalSettings.title} onChange={(e, v) => onChange({...globalSettings, title: v})} />
            <TextField label='Banner Text' value={globalSettings.bannerText} onChange={(e, v) => onChange({...globalSettings, bannerText: v})} />

            <ImageEditor
                value={globalSettings.favicon}
                label={'Favicon'}
                onChange={(v) => onChange({...globalSettings, favicon: v})}
                height={96}
                imageId={'global-favicon'}
                onClear={() => onChange({...globalSettings, favicon: ''})}
                imageType='Storefront'
            />
        </Stack>

        <Accordion useChevron={true} title={'Styles'} teaser={'Configure the overall appearance of your storefront, including fonts, and theme.'} indentWidth={25}>
            <Stack tokens={{childrenGap: '1em'}}>
                <StorefrontThemeSelection theme={globalSettings.theme} onChange={(v) => onChange({...globalSettings, theme: v})} />
                {globalSettings.theme === 'CUSTOM' && <>
                    <CompactColorPicker label='Menu Bar Color' color={globalSettings.menuBarColor ?? '#fff'} onChange={(v) => onChange({...globalSettings, menuBarColor: v})} />
                    <CompactColorPicker label='Menu Bar Text Color' color={globalSettings.menuBarTextColor ?? '#000'} onChange={(v) => onChange({...globalSettings, menuBarTextColor: v})} />
                    <CompactColorPicker label='Background Color' color={globalSettings.backgroundColor ?? '#fff'} onChange={(v) => onChange({...globalSettings, backgroundColor: v})} />
                    <CompactColorPicker label='Text Color' color={globalSettings.textColor ?? '#000'} onChange={(v) => onChange({...globalSettings, textColor: v})} />
                </>}
                <FontPicker label='Header Font' selectedFont={globalSettings.headerFont} onChange={(v) => onChange({...globalSettings, headerFont: v})} />
                <FontPicker label='Default Font' selectedFont={globalSettings.defaultFont} onChange={(v) => onChange({...globalSettings, defaultFont: v})} />
                <NumberField value={globalSettings.maxWidth} placeholder={'800'} label='Max Width' suffix={'px'} onChange={(v) => onChange({...globalSettings, maxWidth: v ?? undefined})} />
            </Stack>
        </Accordion>
        <Accordion useChevron={true} title={'Header/Footer Settings'} indentWidth={25} teaser={'Configure the appearance and content of the header and footer that appears on all storefront pages.'}>
            <ImageEditor
                label={'Header Logo'}
                value={globalSettings.headerLogo}
                onChange={(v) => onChange({...globalSettings, headerLogo: v})}
                height={50}
                imageId={'global-header-logo'}
                onClear={() => onChange({...globalSettings, headerLogo: ''})}
                imageType='Storefront'
            />
            <Stack tokens={{childrenGap: '1em'}}>
                <TextField label='Header Link' value={globalSettings.headerLink ?? ''} onChange={(e, v) => onChange({...globalSettings, headerLink: v ?? ''})} />
                <BooleanDropdown label='Default Use Header Search' value={globalSettings.useHeaderSearch ?? false} onChange={(v) => onChange({...globalSettings, useHeaderSearch: v ?? false})} />
                <BooleanDropdown label='Default Include Footer' value={globalSettings.includeFooter ?? false} onChange={(v) => onChange({...globalSettings, includeFooter: v ?? false})} />
                {globalSettings.includeFooter &&
                <div>
                    <RichTextField label='Footer Contact Details' value={globalSettings.footerContactDetails ?? ''} onChange={(v) => onChange({...globalSettings, footerContactDetails: v ?? ''})} />
                    <TextField label='Footer Facebook' prefix={'facebook.com/'} value={globalSettings.footerFacebook ?? ''} onChange={(e, v) => onChange({...globalSettings, footerFacebook: v ?? ''})} />
                    <TextField label='Footer Youtube' prefix={'youtube.com/'} value={globalSettings.footerYoutube ?? ''} onChange={(e, v) => onChange({...globalSettings, footerYoutube: v ?? ''})} />
                    <TextField label='Footer LinkedIn' prefix={'linkedin.com/in/'} value={globalSettings.footerLinkedIn ?? ''} onChange={(e, v) => onChange({...globalSettings, footerLinkedIn: v ?? ''})} />
                    <TextField label='Footer Twitter' prefix={'twitter.com/'} value={globalSettings.footerTwitter ?? ''} onChange={(e, v) => onChange({...globalSettings, footerTwitter: v ?? ''})} />
                    <TextField label='Footer X' prefix={'x.com/'} value={globalSettings.footerX ?? ''} onChange={(e, v) => onChange({...globalSettings, footerX: v ?? ''})} />
                    <TextField label='Footer Bluesky' prefix={'bsky.app/profile/'} value={globalSettings.footerBluesky ?? ''} onChange={(e, v) => onChange({...globalSettings, footerBluesky: v ?? ''})} />
                    <TextField label='Footer Phone' value={globalSettings.footerPhone ?? ''} onChange={(e, v) => onChange({...globalSettings, footerPhone: v ?? ''})} />
                    <TextField label='Footer Email' value={globalSettings.footerEmail ?? ''} onChange={(e, v) => onChange({...globalSettings, footerEmail: v ?? ''})} />
                    <Label>Footer Logo</Label>
                    <ImageEditor
                        label={'Footer Logo'}
                        value={globalSettings.footerLogo}
                        onChange={(v) => onChange({...globalSettings, footerLogo: v})}
                        height={50}
                        imageId={'global-footer-logo'}
                        onClear={() => onChange({...globalSettings, footerLogo: ''})}
                        imageType='Storefront'
                    />
                    <RichTextField label='Footer Text' value={globalSettings.footerText ?? ''} onChange={(v) => onChange({...globalSettings, footerText: v ?? ''})} />
                </div>}
            </Stack>
        </Accordion>

        <Accordion useChevron={true} indentWidth={25}  title={'Order Settings'} teaser={'Configure settings that impact the ordering experience, including order notes, email content, and terms and conditions.'}>
            <Stack tokens={{childrenGap: '1em'}}>
                <BooleanDropdown value={globalSettings.requireNotes ?? false} onChange={(v) => onChange({...globalSettings, requireNotes: v})} label={'Use Notes'} />
                <DataField
                    toolTip={'When enabled, Notes provides a dynamic input for the customer to provide additional information about their order. Use this field to provide a label for the Notes input field which will appear when the customer goes to check out from storefront.'}
                    label='Notes Label'
                    value={globalSettings.notesLabel}
                    onChange={(e, v) => onChange({...globalSettings, notesLabel: v})} placeholder={'Additional Order Information'}
                />
                <RichTextField
                    toolTip={'Use Notes Instructions to provide detailed instructions about how to fill out the Notes field. Markdown is supported.'}
                    label={'Notes Instructions'}
                    value={globalSettings.notesInstructions}
                    onChange={(v) => onChange({...globalSettings, notesInstructions: v})} />

                <RichTextField
                    label={'Terms and Conditions'}
                    toolTip={globalSettings.paymentProcessor && globalSettings.paymentProcessor != PAYMENT_PROCESSOR_KEYS.NONE ? '' : 'Markdown is supported'}
                    value={globalSettings.termsAndConditions}
                    onChange={(v) => {
                        onChange({...globalSettings, termsAndConditions: v})
                    }}
                />
            </Stack>
        </Accordion>
        <Accordion useChevron={true} indentWidth={25} title={'Email Settings'} teaser={'Configure customer-facing, and error notification emails'}>
            <Stack tokens={{childrenGap: '1em'}}>
                <DataField toolTip={'Specify an email address to be notified if there is an error processing a customer order event'} type='email' label='Order Failure Email' value={globalSettings.orderFailureEmail} onChange={(e,v) => onChange({...globalSettings, orderFailureEmail: v})} />
                <TextField type='email' label='Order Email BCC' value={globalSettings.orderEmail} onChange={(e, v) => onChange({...globalSettings, orderEmail: v})} />
                <TextField label='Customer Email Subject' value={globalSettings.customerEmailSubject} onChange={(e, v) => onChange({...globalSettings, customerEmailSubject: v})} />
                {/* Same collapse as the per-storefront Ordering page: the toggle only chose between
                    this editor and a text box the stock template already renders through
                    {{{customer_email_text}}}, and that text is still the org-wide fallback for any
                    storefront that sets none of its own -- so the setting stays, the field goes.

                    The global toggle was doing even less than the storefront one: the send path reads
                    UseCustomEmailTemplate from the STOREFRONT's settings only, never from here, so
                    this control never decided anything. */}
                <OrderEmailTemplateField
                    label='Default Order Email Template'
                    value={globalSettings.customEmailTemplate}
                    customerEmailText={globalSettings.customerEmailText}
                    onChange={(value) => onChange({
                        ...globalSettings,
                        customEmailTemplate: value,
                        // Kept in step even though nothing reads it here, so the data does not
                        // contradict itself if anything ever starts to.
                        useCustomEmailTemplate: !!value.trim(),
                    })}
                />
            </Stack>
        </Accordion>
        <Accordion useChevron={true} indentWidth={25} title={'Markup Settings'}>
            <Stack tokens={{childrenGap: '1em'}}>
                <NumberField label='Default Markup' decimal={true} value={globalSettings.defaultMarkup} onChange={(v) => onChange({...globalSettings, defaultMarkup: v})} />
                <MarkupTypeSelection isLowestLevel={true} value={globalSettings.defaultMarkupType} onChange={(v) => onChange({...globalSettings, defaultMarkupType: v})} label='Default Markup Type' />
            </Stack>
        </Accordion>
        <Accordion useChevron={true} indentWidth={25}  title={'Shipping Settings'}>
            <Stack tokens={{childrenGap: '1em'}}>
                <NumberField prefix={getCurrencySymbol(globalSettings.currency)} label='Default Shipping Charge' decimal={true} value={globalSettings.defaultShippingCharge} onChange={(v) => onChange({...globalSettings, defaultShippingCharge: v})} />
                <ShippingChargeSelection isLowestLevel={true} value={globalSettings.defaultShippingChargeType} onChange={(v) => onChange({...globalSettings, defaultShippingChargeType: v})} label='Default Shipping Charge Type' />
            </Stack>
        </Accordion>
        <Accordion useChevron={true} title={'Payment Settings'} indentWidth={25}>
            <Stack tokens={{childrenGap: '1em'}}>
                <CurrencySelection value={globalSettings.currency} onChange={(v) => onChange({...globalSettings, currency: v})} />
                <PaymentProcessorDropdown value={globalSettings.paymentProcessor ?? PAYMENT_PROCESSOR_KEYS.NONE} onChange={(v) => onChange({...globalSettings, paymentProcessor: v})} />
                <BooleanDropdown value={globalSettings.collectTax ?? false} onChange={(v) => onChange({...globalSettings, collectTax: v})} label={"Collect Tax"} />
                {globalSettings.collectTax && orgFeatures?.data?.enableSimpleTaxCalculation &&
                <Dropdown
                    label={'Default Tax Code'}
                    placeholder={'Not Set'}
                    selectedKey={globalSettings.defaultTaxCode}
                    options={[...(taxCodes?.data?.map(taxCode => (
                        {
                            key: taxCode.name ?? '',
                            text: `${taxCode.name ?? ''} (${taxCode.rate?.toString()}%)`
                        }
                    )) ?? [])
                    ]}
                    onChange={(e,d) => {
                        if (d) {
                            onChange({...globalSettings, defaultTaxCode: d.key.toString()});
                        }
                    }}
                />}
            </Stack>
        </Accordion>
        <Accordion useChevron={true} title={'Other Settings'} indentWidth={25} >
            <Stack tokens={{childrenGap: '1em'}}>
                <Dropdown
                    placeholder={"Select Vendors"}
                    multiSelect={true}
                    label={"Storefront Vendor Filter"}
                    selectedKeys={globalSettings.storefrontVendors}
                    options={vendors?.data?.items?.map(vendor => ({ key: vendor, text: vendor })) ?? []}
                    onChange={onChangeVendors}
                    disabled={vendors.isLoading || vendors.isFetching}
                />
                <Dropdown
                    placeholder={"Select Filter Strategy"}
                    multiSelect={false}
                    label={"Vendor Filter Strategy"}
                    selectedKey={globalSettings.vendorFilterStrategy ?? "include"}
                    options={filterStrategyOptions()}
                    onChange={(e, v) => {onChange({...globalSettings, vendorFilterStrategy: v?.key.toString() })}}
                />
                <ToggleField
                    toolTip={'If enabled, the Manufacturer Part Number (MFP) will be displayed on the Product Details page in the storefront.'}
                    inline={true}
                    label={'Show MFP on Product Details'}
                    checked={globalSettings.showMfpOnProducts}
                    onChange={(e,v) => {
                        onChange({...globalSettings, showMfpOnProducts: v})
                    }}/>
                <ToggleField
                    toolTip={'Determines the initial state of Show Categories for a new storefront'}
                    inline={true}
                    label={'Default Show Categories'}
                    checked={globalSettings.useCategories}
                    onChange={(e,v) => {
                        onChange({...globalSettings, useCategories: v})
                    }}/>
            </Stack>
        </Accordion>
    </div>
}

export default GlobalStorefrontSettingsForm;